import { useMemo } from "react";
import { useOnboardingDraft } from "../OnboardingContext";
import styles from "../OnboardingWizard.module.css";
import { listTimezones } from "../timezones";
import type { StepProps } from "../OnboardingWizard";

const PERSONALITIES: ReadonlyArray<{ key: string; label: string; meta: string }> = [
  { key: "friendly", label: "Friendly", meta: "Warm, casual, approachable" },
  { key: "professional", label: "Professional", meta: "Polished, formal, on-brand" },
  { key: "direct", label: "Direct", meta: "No fluff, just answers" },
  { key: "witty", label: "Witty", meta: "A little playful, clever turns of phrase" },
  { key: "empathetic", label: "Empathetic", meta: "Patient, supportive, kind" },
  { key: "thoughtful", label: "Thoughtful", meta: "Reflective, takes its time" },
];

const NAME_PLACEHOLDERS = ["e.g. Pebbles, Atlas, Luna, Otis, Iris"];

const AGENT_NAME_MAX = 64;

const slugify = (raw: string): string =>
  raw.trim().toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

export const AgentStep = ({ onNext, onCancel }: StepProps): JSX.Element => {
  const { draft, update } = useOnboardingDraft();
  const trimmed = draft.agentName.trim();
  const slug = useMemo(() => slugify(trimmed), [trimmed]);
  const slugValid = slug.length >= 3 && slug.length <= 30 && /^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(slug);
  const zones = useMemo<ReadonlyArray<string>>(() => listTimezones(), []);
  const tzInList = zones.includes(draft.timezone);
  const tzValid = draft.timezone.trim().length > 0;
  const valid = trimmed.length > 0 && slugValid && tzValid;
  const personalities: ReadonlyArray<string> = draft.personalities ?? [];

  const togglePersonality = (key: string): void => {
    const next = personalities.includes(key)
      ? personalities.filter((p) => p !== key)
      : [...personalities, key];
    update("personalities", next);
  };

  return (
    <section className={styles.card}>
      <div className={styles.stepHeader}>
        <h2 className={styles.stepTitle}>Give your agent a name and a vibe.</h2>
        <p className={styles.stepHint}>
          The name shows up in messages, tool calls, and notifications. The vibe shapes how it talks.
        </p>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="onb-agent-name">Agent name</label>
        <input
          id="onb-agent-name"
          className={styles.input}
          value={draft.agentName}
          onChange={(e) => update("agentName", e.target.value.slice(0, AGENT_NAME_MAX))}
          placeholder={NAME_PLACEHOLDERS[0]}
          maxLength={AGENT_NAME_MAX}
        />
        {trimmed.length > 0 ? (
          slugValid ? (
            <span className={styles.slugPreview}>
              <span className={styles.slugLabel}>Your server name will be</span>
              <code className={styles.slugCode}>{slug}</code>
            </span>
          ) : (
            <span className={styles.errorInline}>
              Use 3–30 chars: letters, numbers, dashes (no leading/trailing dash).
            </span>
          )
        ) : (
          <span className={styles.hint}>We'll normalise to lowercase + dashes for the technical handle.</span>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Personality</label>
        <span className={styles.hint}>Pick one or more; these blend into the agent's tone.</span>
        <div className={styles.optionGrid}>
          {PERSONALITIES.map((p) => {
            const selected = personalities.includes(p.key);
            return (
              <button
                key={p.key}
                type="button"
                className={`${styles.optionTile} ${selected ? styles.optionTileSelected : ""}`}
                onClick={() => togglePersonality(p.key)}
                aria-pressed={selected}
              >
                <span className={styles.optionTitle}>{p.label}</span>
                <span className={styles.optionMeta}>{p.meta}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="onb-timezone">Timezone</label>
        <span className={styles.hint}>
          Your agent uses this to anchor "today", schedule reminders, and avoid waking you up at 3am. We've
          guessed from your browser, so feel free to change it.
        </span>
        <select
          id="onb-timezone"
          className={styles.select}
          value={draft.timezone}
          onChange={(e) => update("timezone", e.target.value)}
        >
          {!tzInList && draft.timezone && (
            <option value={draft.timezone}>{draft.timezone}</option>
          )}
          {zones.map((tz) => (
            <option key={tz} value={tz}>{tz.replace(/_/g, " ")}</option>
          ))}
        </select>
      </div>

      <div className={styles.footer}>
        <button type="button" className={styles.secondaryBtn} onClick={onCancel}>Cancel</button>
        <button type="button" className={styles.primaryBtn} onClick={onNext} disabled={!valid}>Next</button>
      </div>
    </section>
  );
};
